import { useState, useRef } from 'react';

/**
 * Get the minimum height needed to contain all content at a given width.
 * Uses scrollHeight which accounts for content overflow.
 */
function getContentMinHeight(nodeElement, testWidth, baseMinHeight) {
  if (!nodeElement) return baseMinHeight;
  
  // Temporarily set the width and remove height constraint to measure content
  const originalWidth = nodeElement.style.width;
  const originalHeight = nodeElement.style.height;
  const originalMinHeight = nodeElement.style.minHeight;
  const originalOverflow = nodeElement.style.overflow;
  
  nodeElement.style.width = `${testWidth}px`;
  nodeElement.style.height = 'auto';
  nodeElement.style.minHeight = '0';
  nodeElement.style.overflow = 'visible';
  
  // scrollHeight gives us the full content height
  const contentHeight = nodeElement.scrollHeight;
  
  // Restore original styles
  nodeElement.style.width = originalWidth;
  nodeElement.style.height = originalHeight;
  nodeElement.style.minHeight = originalMinHeight;
  nodeElement.style.overflow = originalOverflow;
  
  return Math.max(baseMinHeight, contentHeight);
}

/**
 * Hook for managing node resize interactions
 * @param {Object} options
 * @param {React.RefObject} options.nodeRef - Reference to the node element
 * @param {string} options.taskId - Task ID for the resize callback
 * @param {Function} options.onResize - Callback when resize occurs (during drag)
 * @param {Function} options.onResizeEnd - Callback when resize completes (for undo recording)
 * @param {Function} options.onResetSize - Callback to reset custom size (double-click)
 * @param {number} options.minWidth - Minimum width (default: 200)
 * @param {number} options.minHeight - Minimum height (default: 112)
 * @param {boolean} options.maintainAspectRatio - Keep width and height equal (default: false)
 * @param {Function} options.getZoom - Function to get current zoom level (default: () => 1)
 * @param {Function} options.getPosition - Function to get current node position (default: () => ({ x: 0, y: 0 }))
 */
export function useNodeResize({ 
  nodeRef, 
  taskId, 
  onResize,
  onResizeEnd,
  onResetSize,
  minWidth = 200, 
  minHeight = 112, 
  maintainAspectRatio = false,
  getZoom = () => 1,
  getPosition = () => ({ x: 0, y: 0 }),
}) {
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef(null);
  const lastResizeRef = useRef(null); // Track the last resize values for onResizeEnd
  const lastClickTimeRef = useRef(0); // Track for double-click detection

  const handleResizeMouseDown = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!nodeRef.current) return;
    
    // Double-click detection: reset to auto-size
    const now = Date.now();
    if (now - lastClickTimeRef.current < 300 && onResetSize) {
      lastClickTimeRef.current = 0;
      onResetSize(taskId);
      return;
    }
    lastClickTimeRef.current = now;
    
    // Use offsetWidth/offsetHeight for actual CSS dimensions (unaffected by zoom)
    const startWidth = nodeRef.current.offsetWidth;
    const startHeight = nodeRef.current.offsetHeight;
    
    // Get the current position at the start of resize
    const startPosition = getPosition();
    
    resizeStartRef.current = {
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth,
      startHeight,
      startPositionX: startPosition.x,
      startPositionY: startPosition.y,
      minWidth,
      minHeight,
      maintainAspectRatio,
      nodeElement: nodeRef.current,
    };
    
    setIsResizing(true);
    
    const handleMouseMove = (moveEvent) => {
      if (!resizeStartRef.current) return;
      
      const { 
        direction, startX, startY, startWidth, startHeight, 
        startPositionX, startPositionY,
        minWidth, minHeight, maintainAspectRatio, nodeElement
      } = resizeStartRef.current;
      
      // Get current zoom and convert screen delta to canvas delta
      const zoom = getZoom();
      const deltaX = (moveEvent.clientX - startX) / zoom;
      const deltaY = (moveEvent.clientY - startY) / zoom;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      let newPositionX = startPositionX;
      let newPositionY = startPositionY;
      
      // Calculate new dimensions based on direction
      // East (right edge): width increases with positive deltaX, position unchanged
      if (direction.includes('e')) {
        newWidth = Math.max(minWidth, startWidth + deltaX);
      }
      // West (left edge): width increases with negative deltaX, position shifts left
      if (direction.includes('w')) {
        const widthChange = -deltaX; // Dragging left (negative delta) increases width
        newWidth = Math.max(minWidth, startWidth + widthChange);
        // Position shifts by the actual width change (clamped)
        const actualWidthChange = newWidth - startWidth;
        newPositionX = startPositionX - actualWidthChange;
      }
      
      // Get the content-aware minimum height for the current width
      const contentMinHeight = getContentMinHeight(nodeElement, newWidth, minHeight);
      
      // South (bottom edge): height increases with positive deltaY, position unchanged
      if (direction.includes('s')) {
        newHeight = Math.max(contentMinHeight, startHeight + deltaY);
      }
      // North (top edge): height increases with negative deltaY, position shifts up
      if (direction.includes('n')) {
        const heightChange = -deltaY; // Dragging up (negative delta) increases height
        newHeight = Math.max(contentMinHeight, startHeight + heightChange);
        // Position shifts by the actual height change (clamped)
        const actualHeightChange = newHeight - startHeight;
        newPositionY = startPositionY - actualHeightChange;
      }
      
      // If only resizing width (e or w), also enforce content min height
      if (!direction.includes('s') && !direction.includes('n')) {
        newHeight = Math.max(contentMinHeight, newHeight);
      }
      
      // Maintain aspect ratio (1:1) if requested - use the larger dimension change
      if (maintainAspectRatio) {
        const minSize = Math.max(minWidth, contentMinHeight);
        const size = Math.max(newWidth, newHeight);
        const clampedSize = Math.max(minSize, size);
        
        // Recalculate positions for square resizing
        if (direction.includes('w')) {
          newPositionX = startPositionX - (clampedSize - startWidth);
        }
        if (direction.includes('n')) {
          newPositionY = startPositionY - (clampedSize - startHeight);
        }
        
        newWidth = clampedSize;
        newHeight = clampedSize;
      }
      
      const resizeData = { 
        width: Math.round(newWidth), 
        height: Math.round(newHeight),
        // Pass absolute new position (not deltas)
        newX: Math.round(newPositionX),
        newY: Math.round(newPositionY),
        // Also pass flags to indicate if position changed
        positionChanged: newPositionX !== startPositionX || newPositionY !== startPositionY,
      };
      
      // Track last resize for onResizeEnd
      lastResizeRef.current = resizeData;
      
      onResize?.(taskId, resizeData);
    };
    
    const handleMouseUp = () => {
      // Call onResizeEnd with start and end values for undo recording
      if (onResizeEnd && resizeStartRef.current && lastResizeRef.current) {
        const { startWidth, startHeight, startPositionX, startPositionY } = resizeStartRef.current;
        const endData = lastResizeRef.current;
        
        // Only record if something actually changed
        if (startWidth !== endData.width || startHeight !== endData.height ||
            startPositionX !== endData.newX || startPositionY !== endData.newY) {
          onResizeEnd(taskId, {
            oldWidth: startWidth,
            oldHeight: startHeight,
            oldX: startPositionX,
            oldY: startPositionY,
            newWidth: endData.width,
            newHeight: endData.height,
            newX: endData.newX,
            newY: endData.newY,
          });
        }
      }
      
      setIsResizing(false);
      resizeStartRef.current = null;
      lastResizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return {
    isResizing,
    handleResizeMouseDown,
  };
}

export default useNodeResize;
